import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../ProfileCommon/Breadcrumb";
import NewHeader from "../../Home/NewHeader";
import InboxCard from "../ProfileCommon/InboxCard";
import {
  useLazyGetInboxListQuery,
  usePostReadInboxMutation,
} from "../../../services/apiSlice";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";

const Inbox = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [getListInbox, { isFetching }] = useLazyGetInboxListQuery();
  const [markAllAsRead] = usePostReadInboxMutation();

  const [messages, setMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // const [totalMessages, setTotalMessages] = useState(0);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const observerRef = useRef(null);
  const pollingRef = useRef(null);
  const isLoadingMore = useRef(false);

  const fetchInboxData = useCallback(
    async (page, isInitialLoad = false) => {
      // Tránh multiple calls cùng lúc
      if (isLoadingMore.current && !isInitialLoad) return;

      try {
        isLoadingMore.current = true;

        const response = await getListInbox({
          page: page,
          limit: 5, // Mỗi page load 5 items
        }).unwrap();

        const newMessages = Array.isArray(response?.data) ? response.data : [];
        const total = response?.total || 0;
        // setTotalMessages(total);
        if (newMessages.length === 0) {
          setHasMore(false);
          return;
        }

        if (isInitialLoad) {
          setMessages(newMessages);
          setCurrentPage(1);

          setHasMore(newMessages.length === 5 && total > newMessages.length);
        } else {
          setMessages((prevMessages) => {
            const existingIds = new Set(prevMessages.map((msg) => msg.id));
            const uniqueNewMessages = newMessages.filter(
              (msg) => !existingIds.has(msg.id)
            );
            const combinedMessages = [...prevMessages, ...uniqueNewMessages];
            setHasMore(
              newMessages.length === 5 && combinedMessages.length < total
            );

            return combinedMessages;
          });
        }
      } catch (error) {
        console.error("Error fetching inbox:", error);
        setHasMore(false);
      } finally {
        isLoadingMore.current = false;
      }
    },
    [getListInbox]
  );

  const markMessagesAsRead = useCallback(async () => {
    try {
      await markAllAsRead().unwrap();
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  }, [markAllAsRead]);

  // Initial load khi component mount
  useEffect(() => {
    const initialLoad = async () => {
      await fetchInboxData(1, true);
      await markMessagesAsRead();
      setInitialLoaded(true);
    };

    initialLoad();

    pollingRef.current = setInterval(() => {
      fetchInboxData(1, true);
    }, 60 * 1000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchInboxData, markMessagesAsRead]);

  const lastMessageElementRef = useCallback(
    (node) => {
      if (!node || isFetching || !hasMore || !initialLoaded) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            hasMore &&
            !isFetching &&
            !isLoadingMore.current
          ) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchInboxData(nextPage, false);
          }
        },
        {
          threshold: 0.1,
          rootMargin: "100px",
        }
      );

      observerRef.current.observe(node);
    },
    [isFetching, hasMore, initialLoaded, currentPage, fetchInboxData]
  );

  // Cleanup observer khi component unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="flex flex-col mx-auto w-full bg-white max-w-[480px] min-h-[912px]">
      <LoadingCommon statusLoading={isFetching} />
      <div className=" fixed top-0 left-0 w-full z-50">
        <NewHeader />
      </div>
      <div className="h-[30px]"></div>
      <div className="flex flex-col flex-1 gap-3 py-3.5 px-[30px] mt-4">
        <Breadcrumb
          breadCrumbTiltle={t("profile.inbox")}
          onClickBack={() => navigate("/profile")}
        />

        {messages.length > 0 ? (
          messages.map((message, index) => (
            <div
              key={message.id || index}
              ref={
                index === messages.length - 1 && hasMore && initialLoaded
                  ? lastMessageElementRef
                  : undefined
              }
              className="mb-1"
            >
              <InboxCard
                status={message.notification_type}
                title={message.description}
                timestamp={message.updated_at}
                linkHref="/profile/support/contactUS"
              />
            </div>
          ))
        ) : initialLoaded ? (
          <div className="text-center text-gray-500 py-4">
            {t("inbox.noMessages")}
          </div>
        ) : null}

        {isFetching && messages.length > 0 && (
          <div className="text-center py-4">
            <div className="text-sm text-gray-500">Loading more...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inbox;
